import { ComponentType } from '@Types';

// Task is keyed on uploadId (as in fetchMetadata): its dataSource isn't
// updated when a new task is picked, so the generic key would miss a
// same-named field on a reconnected task.
export const getMetadataDirtyKey = (
	type: ComponentType,
	field: string,
	dataSource: string,
	uploadId: string,
): string =>
	type === ComponentType.Task
		? `${field}-${type}-${uploadId}`
		: `${field}-${type}-${dataSource}`;

// The key a fetched result is stored under, per source type (mirrors the
// ternary in fetchMetadata).
export const getMetadataKey = (
	type: ComponentType,
	field: string,
	dataSource: string,
	uploadId: string,
	failureDefinitionId: string,
	deteriorationModelId: string,
): string => {
	const sourceId: Partial<Record<ComponentType, string>> = {
		[ComponentType.Task]: uploadId,
		[ComponentType.FailureDefinition]: failureDefinitionId,
		[ComponentType.DeteriorationModel]: deteriorationModelId,
	};
	return `${field}-${type}-${sourceId[type] ?? dataSource}`;
};

// True once a metadata fetch has settled. Simulation never fetches, so its
// store is always treated as resolved.
export const isMetadataFetchResolved = (
	metadataStore: { isLoading?: boolean } | undefined,
	fetchingMetadata: boolean,
	type: ComponentType,
): boolean =>
	!!metadataStore &&
	!metadataStore.isLoading &&
	(fetchingMetadata || type === ComponentType.Simulation);

// While reconnecting, an incomplete source (e.g. the field was cleared after
// the data source changed) invalidates the saved metadata key, both on the
// component prop (checked by the field effect) and in the screen's own
// unitMetadataName state (checked by fetchMetadata). Without this, switching
// back to the original source matches the saved key, skips the refetch, and
// leaves the score table hidden with Update still enabled.
export const invalidateSavedMetadataKey = (
	component: { unitMetadataName?: string },
	isReconnecting: boolean,
	isValidSource: boolean,
	clearKeyState: () => void,
): void => {
	if (isReconnecting && !isValidSource) {
		component.unitMetadataName = undefined;
		clearKeyState();
	}
};
